import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';
import { Button } from './button';

export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number | null; // null means unknown total pages
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  showEntryCount?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  showPageSizeSelector = true,
  showEntryCount = true,
  className = '',
  children,
}) => {
  const startEntry = currentPage * pageSize + 1;

  const canGoPrevious = currentPage > 0;
  const canGoNext =
    totalPages === null
      ? totalCount === pageSize
      : currentPage < totalPages - 1;
  const hasUnknownTotal = totalPages === null;

  const endEntry = hasUnknownTotal
    ? totalCount === 0
      ? 0
      : startEntry + totalCount - 1
    : Math.min((currentPage + 1) * pageSize, totalCount);

  return (
    <div
      className={`flex items-center justify-between px-4 py-3 bg-white border border-table-border rounded-lg ${className}`}
    >
      {children && (
        <div className="flex items-center space-x-2 justify-start">
          {children}
        </div>
      )}
      {showEntryCount && (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-text-secondary">
            {hasUnknownTotal && totalCount === 0
              ? currentPage === 0
                ? 'Keine Einträge'
                : 'Keine Einträge auf dieser Seite'
              : `Einträge ${startEntry} bis ${endEntry}${hasUnknownTotal ? '' : ` von ${totalCount}`}`}
          </span>
        </div>
      )}

      <div className="flex items-center space-x-2">
        {showPageSizeSelector && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-secondary">
              Einträge pro Seite:
            </span>
            <select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                onPageChange(0); // Reset to first page when changing page size
              }}
              className="px-2 py-1 border border-table-border rounded text-sm"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-center space-x-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(0)}
            disabled={!canGoPrevious}
            className="h-8 w-8 p-0"
            title="Erste Seite"
          >
            <ChevronLeft className="h-4 w-4" />
            <ChevronLeft className="h-4 w-4 -ml-1" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!canGoPrevious}
            className="h-8 w-8 p-0"
            title="Vorherige Seite"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <span className="px-3 py-1 text-sm text-text-secondary">
            {hasUnknownTotal
              ? `Seite ${currentPage + 1}`
              : `Seite ${currentPage + 1} von ${totalPages}`}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!canGoNext}
            className="h-8 w-8 p-0"
            title="Nächste Seite"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!hasUnknownTotal && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages - 1)}
              disabled={!canGoNext}
              className="h-8 w-8 p-0"
              title="Letzte Seite"
            >
              <ChevronRight className="h-4 w-4" />
              <ChevronRight className="h-4 w-4 -ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
